import { useMemo } from 'react'
import { DefaultStockType, ScheduleType } from 'types/schedule'
import { getLatestOpenTime } from 'lib/businessHour'
import { parseTimeToNumber } from 'lib/helper'
import { OpenTimeType } from 'types/store'
import { DayOfWeekType } from 'lib/daysOfWeek'

const useConfirmUpdatedSchedules = (
  schedules: ScheduleType[],
  confirmOpenTime: OpenTimeType,
  addedHolidays: DayOfWeekType[],
  isOpenTimeUpdated: boolean
): ScheduleType[] => {
  // 店舗オープン時刻の変更に伴う受渡解禁時刻の上書き
  return useMemo(() => {
    const _schedules: ScheduleType[] = []
    if (!schedules || !confirmOpenTime || !addedHolidays) return _schedules

    for (let i = 0; i < schedules.length; i = (i + 1) | 0) {
      const schedule = { ...schedules[i] }
      const latestOpenTime = getLatestOpenTime(schedule.days_of_week, confirmOpenTime)

      if (
        // 追加された定休日に受渡可能曜日が含まれていなければ除外
        addedHolidays.every((holiday) => !schedule.days_of_week.includes(holiday)) &&
        // オープン時刻が更新されていなければ除外
        (!isOpenTimeUpdated ||
          // 受渡解禁時刻がオープン時刻よりも遅ければ除外
          schedule.default_stocks.every((defaultStock) => {
            return parseTimeToNumber(defaultStock.pickupable_time) >= parseTimeToNumber(latestOpenTime)
          }))
      )
        continue

      // 定休日に追加された曜日を削除する
      if (addedHolidays.some((holiday) => schedule.days_of_week.includes(holiday))) {
        schedule.days_of_week = schedule.days_of_week.filter((dayOfWeek) => !addedHolidays.includes(dayOfWeek))
      }

      // 受渡可能日がない場合は在庫数を0にする
      if (schedule.days_of_week.length === 0) {
        schedule.default_stocks = schedule.default_stocks.map((defaultStock) => {
          return { pickupable_time: defaultStock.pickupable_time, stock: 0 }
        })
      }

      // 既存の受渡解禁時刻がオープン時刻より遅い場合、オープン時刻で上書きする
      // 上書きの結果、受渡解禁時刻が重複したらマージする
      if (
        schedule.default_stocks.some((defaultStock) => {
          return parseTimeToNumber(defaultStock.pickupable_time) < parseTimeToNumber(latestOpenTime)
        })
      ) {
        schedule.default_stocks = schedule.default_stocks
          .map((defaultStock) => {
            if (parseTimeToNumber(defaultStock.pickupable_time) < parseTimeToNumber(latestOpenTime)) {
              return { pickupable_time: latestOpenTime, stock: defaultStock.stock }
            } else {
              return defaultStock
            }
          })
          .reduce((prev, defaultStock) => {
            const index = prev.findIndex((_) => _.pickupable_time === defaultStock.pickupable_time)
            if (index === -1) {
              return [...prev, defaultStock]
            } else {
              prev[index] = {
                pickupable_time: defaultStock.pickupable_time,
                stock: prev[index].stock + defaultStock.stock,
              }
              return prev
            }
          }, [] as DefaultStockType[])
      }
      _schedules.push(schedule)
    }
    return _schedules
  }, [schedules, confirmOpenTime, addedHolidays, isOpenTimeUpdated])
}

export default useConfirmUpdatedSchedules
